import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { scrollToUDS } from '../helpers';
import useEventBus from './useEventBus';
import useTrackEvent from './useTrackEvent';
import {
	NAVIGATE_TO_CTA_DESTINATION,
	SCROLL_THE_PAGE_TO_UDS_SECTION
} from '../constants';
/**
 * Custom hook to handle step click events for CTA buttons/links.
 *
 * @param {string} route - The route to open sdp when the CTA is clicked.
 * @param {string} ctaLabel - The label of the CTA button/link.
 * @param {boolean} [isScrollToUds=false] - Flag to determine if the page should scroll to UDS.
 *
 * @returns {Function} - A click handler function to be used in Step CTA buttons/links.
 *
 * Usage:
 *
 * const handleClick = useStepClickHandler('dashboard', 'CTA Label', true);
 *
 * return (
 *   <button onClick={handleClick}>
 *     Click Me
 *   </button>
 * );
 */
const useStepClickHandler = (route, ctaLabel, isScrollToUds = false) => {
	const [eventBus, isReady] = useEventBus();

	// Redux-based payment info
	const payment = useSelector((state) => state.paymentsInfo) || {};
	const paymentsInfoLatestRef = useRef(payment);
	paymentsInfoLatestRef.current = payment;

	const sendTrackEvent = useTrackEvent();

	const stepCTAClickHandler = useCallback(
		(_, shouldSendTrackEvent = true) => {
			if (isScrollToUds) {
				scrollToUDS();
			} else if (isReady) {
				const pencilingData = paymentsInfoLatestRef.current;
				const paymentsInfo = (pencilingData.dealType) ? {
					paymentsInfo: {
						...pencilingData,
						...(pencilingData.dealType === 'lease' && {
							leaseMileageRange: pencilingData.annualMiles,
						}),
					}
				} : {};

				eventBus.emit('uds:openSdp:request', { route, ...paymentsInfo });
			}

			if (shouldSendTrackEvent) {
				sendTrackEvent({
					element: 'CTA Button/Link',
					elementCTA: ctaLabel,
					result: isScrollToUds
						? SCROLL_THE_PAGE_TO_UDS_SECTION
						: NAVIGATE_TO_CTA_DESTINATION
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isScrollToUds, ctaLabel, route, isReady, eventBus, sendTrackEvent]
	);

	return stepCTAClickHandler;
};

export default useStepClickHandler;
